import { useState } from "react";
import { Link } from "react-router";
import { Unlink } from "lucide-react";
import { useSetCollectionTitleRecordMutation } from "@/api/api";
import { useCollection } from "@/api/hooks";
import { deserializeRecord, intervalNames } from "@/common/record";
import { recordPath } from "@/common/urls";
import { globalReloader } from "@/providers/dbRecordsLoader";
import { Button } from "../button";
import { Field, FieldLabel } from "../field";
import RecordIntervalName from "./RecordIntervalName";

/**
 * The title record, as seen from the collection.
 *
 * Read and unlink only, and the asymmetry with `RecordCollectionLink` is the
 * point: from a record, the picker is over *collections*, which are already
 * fully loaded client-side. From here it would be a picker over every record the
 * user owns, which needs a search endpoint that does not exist. Both directions
 * of linking therefore live on the record.
 */
export default function CollectionTitleRecordInput() {
  const collection = useCollection();
  const [setTitleRecord, { isLoading }] = useSetCollectionTitleRecordMutation();
  const [error, setError] = useState<string | null>(null);

  const record = deserializeRecord(collection?.title_record ?? null);

  const handleUnlink = async () => {
    if (!collection) return;
    setError(null);
    try {
      await setTitleRecord({
        collectionID: collection.id,
        recordID: null,
      }).unwrap();
      // The timeline reads through DBRecordsLoader rather than RTK Query, so
      // the tag invalidation above does not reach it.
      globalReloader.reload();
    } catch {
      setError("Could not unlink the record.");
    }
  };

  return (
    <Field>
      <FieldLabel className="uppercase text-[10.5px] tracking-[.08em] font-bold text-muted-foreground">
        Title record
      </FieldLabel>

      {record ? (
        <>
          <div className="flex items-start gap-2 rounded-[10px] border border-border bg-secondary px-3 py-2">
            <span
              aria-hidden
              className="mt-[3px] h-[22px] w-[3px] shrink-0 rounded-full bg-primary"
            />
            <div className="min-w-0 flex-1">
              <Link
                to={recordPath(record)}
                className="block truncate text-[13px] text-foreground hover:underline"
              >
                {record.title || "Untitled record"}
              </Link>
              {"interval" in record && record.interval && (
                <div className="font-mono text-[11px] text-muted-foreground">
                  <RecordIntervalName
                    record={{ ...record, ...intervalNames(record) }}
                  />
                </div>
              )}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              disabled={isLoading}
              onClick={handleUnlink}
              title="Keep both, drop the link between them"
            >
              <Unlink />
            </Button>
          </div>
          <p className="text-[11px] leading-[1.5] text-muted-foreground">
            This record stands for the collection on other timelines. It is not
            filed in here — that is what keeps it from listing inside itself.
          </p>
        </>
      ) : (
        <p className="text-[11px] leading-[1.5] text-muted-foreground">
          None. Open the record that should head this collection, and under
          “Collection of its own” choose <strong>Link existing</strong>. Picking
          it from here would mean searching every record you own; picking the
          collection from the record is the same choice, the other way round.
        </p>
      )}

      {error && <p className="text-[11px] text-destructive">{error}</p>}
    </Field>
  );
}
